<template>
  <div class="ab-report__content">
    <div class="content__loading" v-if="isLoading">
      <ui-loading-circle />
    </div>
    <div v-else>
      <!-- a/b capture image -->
      <section class="content__capture">
        <div v-for="abType in abTypes" :key="abType"
             class="box-layout" :class="`${abType}-page`"
             :ref="`${abType}HalfWidthBox`">
          <div class="capture__info">
            <div class="label">
              <span>{{ abType | upperFirst }}</span>
            </div>
            <div class="info">
              <p class="infos">
                <span class="name" :title="abData[abType].pageName">{{ abData[abType].pageName }}</span>
                <span class="traffic">{{ abHalfTraffic }}%</span>
              </p>
              <p class="url">
                <a :href="abData[abType].url" target="_blank"><i class="icon icon--link-size16"></i></a> {{ abData[abType].url }}
              </p>
            </div>
          </div>
          <figure v-if="imageData[abType]" class="capture__img" ref="figure" :style="{height: figHeight + 'px'}">
            <img :id="`${abType}PageCapture`" :src="imageData[abType].url" :alt="`${abType} page capture image`" v-if="imageData[abType].url">
          </figure>
        </div>
      </section>

      <!-- traffic ratio -->
      <section class="content__traffic-ratio">
        <div class="box-layout">
          <div>
            <span class="sub-title">{{ $t('ab.report.default.trafficRatio.label') }}</span>
            <span class="traffic-ratio__formula">
              {{ trafficSummary }}
            </span>
          </div>
          <div class="traffic-ratio__bar" ref="trafficRatioWrapper">
            <div v-for="abType in abTypes" :key="abType"
                 :class="[`${abType}-page__area`, {noBarWidth: trafficBarWidth < 1.6}]"
                 :style="{width: trafficBarWidth + '%'}">
              <span :class="`value__${abType}-page`">{{ abHalfTraffic }}%</span></div>
            <span class="value__exclude" v-if="abTraffic !== 100">{{ 100 - abTraffic }}%</span>
          </div>
        </div>
      </section>

      <ABSummary
        :abTypes="abTypes"
        :aAnalyticsData="aAnalyticsData"
        :bAnalyticsData="bAnalyticsData"
        :aHeatmapData="aHeatmapData || {}"
        :bHeatmapData="bHeatmapData || {}"/>

      <!-- conversion rate top5 -->
      <section class="content__conversion-rate">
        <p class="title">{{ $t('ab.report.default.cvr.title') }}</p>
        <div class="box-layout">
          <table>
            <colgroup>
              <col width="23%">
              <col width="*">
              <col width="23%">
            </colgroup>
            <thead>
            <tr>
              <th class="th__a">{{ $t('ab.report.default.cvr.label.aPage') }}</th>
              <th class="th__improve">{{ $t('ab.report.default.cvr.label.improvement') }}</th>
              <th class="th__b">{{ $t('ab.report.default.cvr.label.bPage') }}</th>
            </tr>
            </thead>
            <tbody v-if="conversionList.length">
            <tr v-for="(item, index) in conversionList" :key="index">
              <td class="td__a-value" :class="item.compare">
                {{ item.a.conversion }} %
              </td>
              <td class="td__separate">
                <div>
                  <a :href="getConversionUrl(item.url, applyData.hostname)" target="_blank">
                    {{ getConversionUrl(item.url, applyData.hostname) }}
                  </a>
                </div>
                <div>
                  <div class="bar--wrap">
                  <span class="a-bar"><i :style="{width: item.a.chartPercentage + '%'}">
                  </i></span>
                    <span class="divide"></span>
                    <span class="b-bar"><i :style="{width: item.b.chartPercentage + '%'}"></i></span>
                  </div>
                </div>
              </td>
              <td class="td__b-value" :class="item.compare">
                {{ item.b.conversion }} %
              </td>
            </tr>
            </tbody>
            <tbody v-else>
            <tr>
              <td colspan="3">-</td>
            </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- click per pv -->
      <section class="content__click-per-pv">
        <p class="title">{{ $t('ab.report.default.clickPerPv.title') }}</p>
        <div class="box-layout">
          <table>
            <colgroup>
              <col width="36%">
              <col width="*">
              <col width="36%">
            </colgroup>
            <thead>
            <tr>
              <th class="th__a">{{ $t('ab.report.default.clickPerPv.label.aPage') }}</th>
              <th class="th__ranking">{{ $t('common.rank') }}</th>
              <th class="th__b">{{ $t('ab.report.default.clickPerPv.label.bPage') }}</th>
            </tr>
            </thead>
            <tbody v-if="clickList.length">
            <tr v-for="(item, index) in clickList" :key="index">
              <td class="td__a-value">
                <figure v-if="item['a']">
                  <crop-image :url="imageData.a.url"
                              :imageWidth="imageData.a.width"
                              :imageHeight="imageData.a.height"
                              :width="item['a'].width"
                              :height="item['a'].height"
                              :offset="{ x: item['a'].left, y: item['a'].top }"
                              :maxWidth="300"
                              :maxHeight="42"/>
                </figure>
                <span v-else>-</span>
              </td>
              <td class="td__ranking">
                <div class="ranking--wrap">
                  <div class="a-page-value">
                    <span :class="item['compare']" v-if="item['a']">
                      {{ item['a'].clickPerPv | fixed(2) | clicks }}
                    </span>
                    <span v-else>-</span>
                  </div>
                  <div class="rank">
                    <span>{{ index + 1 | rank }}</span>
                  </div>
                  <div class="b-page-value">
                    <span :class="item['compare']" v-if="item['b']">
                      {{ item['b'].clickPerPv | fixed(2) | clicks }}
                    </span>
                    <span v-else>-</span>
                  </div>
                </div>
              </td>
              <td class="td__b-value">
                <figure v-if="item['b']">
                  <crop-image :url="imageData.b.url"
                              :imageWidth="imageData.b.width"
                              :imageHeight="imageData.b.height"
                              :width="item['b'].width"
                              :height="item['b'].height"
                              :offset="{ x: item['b'].left, y: item['b'].top }"
                              :maxWidth="300"
                              :maxHeight="42"/>
                </figure>
                <span v-else>-</span>
              </td>
            </tr>
            </tbody>
            <tbody v-else>
            <tr>
              <td class="td__a-value">-</td>
              <td class="td__ranking">
                <div class="ranking--wrap">
                  <div class="a-page-value">-</div>
                  <div class="rank">-</div>
                  <div class="b-page-value">-</div>
                </div>
              </td>
              <td class="td__b-value">-</td>
            </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
    <slot v-if="!isLoading" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { getHeatmapData, getAnalyticsData, getCaptureImageUrl, getDailyDevice } from '@/api/report'
import { normalizeClickData } from '@/libs/report-data-normalizer'
import { getPercentage } from '@/libs/utils'
import { getConversionUrl } from '@/libs/analytics-helper'
import ABSummary from '@/components/report-ab/ABSummary'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle.vue'
import CropImage from '@/components/ui/CropImage'

export default {
  components: {
    ABSummary,
    UiLoadingCircle,
    CropImage
  },
  props: [
    'abList',
    'aData',
    'bData'
  ],
  data () {
    return {
      isLoading: true,
      device: 'desktop',
      timestamp: new Date().getTime(),
      analyticsData: {
        a: null,
        b: null
      },
      heatmapData: {
        a: null,
        b: null
      },
      imageData: {
        a: null,
        b: null
      },
      coordData: {
        a: [],
        b: []
      },
      figHeight: 0,
      conversionList: [],
      clickList: []
    }
  },
  computed: {
    ...mapState([
      'reportPeriod',
      'applyData'
    ]),
    ...mapGetters([
      'abUsedTraffic'
    ]),
    aid () {
      return this.$route.params.aid
    },
    abAid () {
      return this.$route.params.abAid
    },
    abData () {
      return {
        a: this.aData,
        b: this.bData
      }
    },
    aAnalyticsData () {
      return this.analyticsData.a
    },
    bAnalyticsData () {
      return this.analyticsData.b
    },
    aHeatmapData () {
      return this.heatmapData.a
    },
    bHeatmapData () {
      return this.heatmapData.b
    },
    abTypes () {
      return ['a', 'b']
    },
    abTraffic () {
      return this.bData.traffic
    },
    abHalfTraffic () {
      return this.abTraffic / 2
    },
    trafficBarWidth () {
      return this.abHalfTraffic
    },
    trafficSummary () {
      const abTraffic = this.abTraffic
      const abHalfTraffic = this.abHalfTraffic
      const pageLabel = this.$t('common.page')
      const excludeText = abTraffic !== 100 ? `+ ${this.$t('ab.report.default.excludeTest')}(${100 - abTraffic}%)` : ''
      return `A ${pageLabel}(${abHalfTraffic}%) + B ${pageLabel}(${abHalfTraffic}%) ${excludeText} = 100%`
    }
  },
  methods: {
    loadData () {
      this.isLoading = true

      const getDailyDeviceParam = {
        aid: this.aid,
        startDate: this.reportPeriod.start,
        endDate: this.reportPeriod.end,
        bab: false
      }
      getDailyDevice(getDailyDeviceParam).then(result => {
        this.device = this.getDefaultDevice(result)

        const analyticsParam = {
          aid: this.abAid,
          startDate: this.reportPeriod.start,
          endDate: this.reportPeriod.end
        }
        const analyticsParamA = {
          ...analyticsParam,
          bab: false
        }
        const analyticsParamB = {
          ...analyticsParam,
          bab: true
        }
        const heatmapParam = {
          event: 'click',
          aid: this.abAid,
          device: this.device,
          startDate: this.reportPeriod.start,
          endDate: this.reportPeriod.end,
          refer: 'all',
          hidden: false
        }
        const heatmapParamA = {
          ...heatmapParam,
          bab: false
        }
        const heatmapParamB = {
          ...heatmapParam,
          bab: true
        }

        Promise.all([
          getAnalyticsData(analyticsParamA),
          getAnalyticsData(analyticsParamB),
          getHeatmapData(heatmapParamA),
          getHeatmapData(heatmapParamB)
        ]).then(result => {
          this.analyticsData.a = result[0].analytics_data
          this.analyticsData.b = result[1].analytics_data
          this.heatmapData.a = result[2]
          this.heatmapData.b = result[3]
          this.setCoordsData().then(() => {
            this.setConversionList()
            this.setClickList()
            this.isLoading = false
          }).catch(err => {
            throw err
          })
        }).catch(err => {
          throw err
        })
      }).catch(err => {
        console.error(err)
        this.isLoading = false
      })
    },
    captureImageParams (abType) {
      const captureAid = abType === 'a' ? this.aid : this.abAid
      return {
        aid: captureAid,
        device: this.device,
        startDate: this.reportPeriod.start,
        endDate: this.reportPeriod.end,
        timestamp: this.timestamp
      }
    },
    setCoordsData () {
      return Promise.all([
        this.getCaptureImageData(this.captureImageParams('a'), this.timestamp),
        this.getCaptureImageData(this.captureImageParams('b'), this.timestamp)
      ]).then(result => {
        this.imageData = {
          a: result[0],
          b: result[1]
        }
        for (const abType of this.abTypes) {
          if (this.imageData[abType] && this.heatmapData[abType]) {
            const data = this.heatmapData[abType]
            const pvTotal = this.analyticsData[abType].pvTotal
            const imageData = this.imageData[abType]
            const coordsData = normalizeClickData(data, pvTotal, this.device,
              imageData.width, imageData.height)
            this.coordData[abType] = coordsData
          } else {
            this.coordData[abType] = {}
          }
        }
      })
    },
    getCaptureImageData (params, timestamp) {
      return new Promise((resolve, reject) => {
        getCaptureImageUrl(params, timestamp).then(imageUrl => {
          if (!imageUrl) {
            return resolve(null)
          }
          const img = document.createElement('img')
          img.addEventListener('load', event => {
            const el = event.target
            resolve({
              url: imageUrl,
              width: el.width,
              height: el.height
            })
          })
          img.src = imageUrl
        }).catch(err => {
          reject(err)
        })
      })
    },
    setConversionList () {
      const data = {}
      let maxConversion = 0

      for (const abType of this.abTypes) {
        for (const item of this.analyticsData[abType].cvr) {
          if (!data[item.cvr]) {
            data[item.cvr] = {
              a: 0,
              b: 0
            }
          }
          data[item.cvr][abType] += item.count
        }
      }

      const items = Object.entries(data).map(([url, item]) => {
        const aConversion = getPercentage(item.a, this.analyticsData.a.pvTotal, 1)
        const bConversion = getPercentage(item.b, this.analyticsData.b.pvTotal, 1)
        maxConversion = Math.max(maxConversion, aConversion, bConversion)

        return {
          url,
          compare: '',
          a: {
            count: item.a,
            conversion: aConversion
          },
          b: {
            count: item.b,
            conversion: bConversion
          },
          conversionDiff: Math.abs(aConversion - bConversion)
        }
      }).sort((a, b) => {
        return b.conversionDiff - a.conversionDiff
      }).slice(0, 5)

      for (const item of items) {
        if (item.a.conversion !== item.b.conversion) {
          item.compare = item.a.conversion > item.b.conversion ? 'largeA' : 'largeB'
        }
        item.a.chartPercentage = getPercentage(item.a.conversion, maxConversion, 1)
        item.b.chartPercentage = getPercentage(item.b.conversion, maxConversion, 1)
      }
      this.conversionList = items
    },
    setClickList () {
      const items = []
      const sortFn = (a, b) => {
        return b.clickCount - a.clickCount
      }

      let aItems = []
      let bItems = []

      if (this.coordData.a.coords) {
        aItems = Object.values(this.coordData.a.coords).sort(sortFn)
      }

      if (this.coordData.b.coords) {
        bItems = Object.values(this.coordData.b.coords).sort(sortFn)
      }

      let itemSize = Math.max(aItems.length, bItems.length)
      itemSize = Math.min(itemSize, 5)

      for (let i = 0; i < itemSize; i++) {
        const item = {
          a: aItems[i],
          b: bItems[i],
          compare: ''
        }

        items.push(item)
      }
      this.clickList = items
    },
    getDefaultDevice (dailyDevice) {
      const deviceList = ['desktop', 'tablet', 'phone']
      const pvData = {}

      for (const device of deviceList) {
        pvData[device] = 0
      }

      for (const item of dailyDevice) {
        for (const [device, data] of Object.entries(item.device)) {
          pvData[device] += data.pv
        }
      }
      const items = deviceList.map(device => {
        return {
          device,
          pv: pvData[device]
        }
      }).sort((a, b) => b.pv - a.pv)
      return items[0].device
    }
  },
  watch: {
    reportPeriod (value) {
      this.loadData()
    }
  },
  created () {
    this.getConversionUrl = getConversionUrl
    this.loadData()
  },
  mounted () {
    let boxWidth = 0

    this.timer = window.setInterval(() => {
      const boxInnerSpace = 145 // padding(25 + 25) + margin(20) + figure 이외의 offsetHeight(75)
      if (this.$refs.aHalfWidthBox && !this.isLoading) {
        const width = this.$refs.aHalfWidthBox[0].offsetWidth

        if (boxWidth !== width) {
          boxWidth = width
          this.figHeight = boxWidth * 0.8 - boxInnerSpace
        }
      }
    }, 100)
  },
  destroyed () {
    window.clearInterval(this.timer)
  }
}
</script>

<style lang="scss" scoped>
.ab-report__content {
  .box-layout {
    border-radius: 14px;
    background-color: #ffffff;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
    padding: 25px;
  }

  section {
    &.content__capture {
      display: flex;
      justify-content: space-between;

      .box-layout {
        width: calc(50% - 15px); // height는 width의 80%
        box-sizing: border-box;

        .capture__info {
          display: flex;
          align-items: center;
          width: 100%;

          .label {
            width: 75px;
            height: 75px;
            border-radius: 5px;
            position: relative;
            margin-right: 20px;
            flex-grow: 0;

            span {
              @include positionXYcenter;
              font-size: 30px;
              font-weight: bold;
              color: #ffffff;
            }
          }

          .info {
            align-items: center;
            max-width: calc(100% - 95px);
            flex-grow: 1;

            .infos {
              display: flex;
              width: 100%;

              .name {
                @include textEllipsis;
                display: inline-block;
                font-size: 15px;
                font-weight: bold;
                color: #444444;
                align-items: center;
                width: 100px;
                flex-grow: 1;
              }

              .traffic {
                font-size: 15px;
                font-weight: bold;
                color: #444444;
                position: relative;
                padding-left: 10px;
                margin-left: 10px;
                display: inline-block;
                vertical-align: top;
                align-items: center;
                flex-grow: 0;

                &:before {
                  @include positionYcenter;
                  left: 0;
                  content: '';
                  display: inline-block;
                  width: 1px;
                  height: 10px;
                  border-left: 1px solid #d8d8d8;
                }
              }
            }

            .url {
              @include textEllipsis;
              position: relative;
              padding-left: 21px;
              margin-top: 14px;
              font-size: 13px;
              color: #444444;

              .icon {
                position: absolute;
                top: calc(50% - 2px);
                left: 0;
                transform: translateY(-50%) rotateY(180deg);
              }
            }
          }
        }

        .capture__img { // height는 width의 60%
          clear: both;
          border-radius: 4px;
          box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.05);
          background-color: #444444;
          margin-top: 20px;
          overflow-y: auto;
          box-sizing: border-box;
          border: 1px solid #e8e8e8;

          img {
            display: inline-block;
            width: 100%;
          }
        }
      }

      .a-page {
        .label {
          background-color: #9fce12;
        }
      }

      .b-page {
        .label {
          background-color: #1199af;
        }
      }
    }
    &.content__traffic-ratio {
      margin-top: 30px;
      .box-layout {
        padding-bottom: 30px;
        color: #444444;
      }
      .sub-title {
        font-size: 15px;
        font-weight: bold;
      }
      .traffic-ratio__formula {
        font-size: 13px;
        float: right;
      }
      .traffic-ratio__bar {
        position: relative;
        margin-top: 30px;
        background-color: #f0f0f0;
        border-radius: 12.5px;
        height: 25px;
        overflow: hidden;

        .value__exclude {
          color: #555;
        }

        .value__exclude,
        .value__a-page,
        .value__b-page {
          @include positionXYcenter;
          font-size: 12px;
          font-weight: bold;
        }
        .a-page__area {
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
          background-color: #9fce12;
          color: #ffffff;

          &.noBarWidth {
            .value__a-page {
              color: #666;
              left: 35px;
            }
          }
        }
        .b-page__area {
          height: 100%;
          position: absolute;
          top: 0;
          right: 0;
          background-color: #1199af;
          color: #ffffff;

          &.noBarWidth {
            .value__b-page {
              color: #666;
              left: -20px;
            }
          }
        }
      }
    }
    &.content__summary {
      margin-top: 70px;
      .title {
        font-size: 22px;
        font-weight: bold;
        color: #444444;
      }
      .box__graph {
        margin-top: 30px;
        padding: 30px 40px;
        text-align: center;

        .button--wrap {
          display: inline-block;
          position: relative;
          text-align: center;
          white-space: nowrap;
          height: 38px;
          border-radius: 20px;
          border: 1px solid #e0e0e0;
          background-color: #fff;
          box-sizing: border-box;

          li {
            cursor: pointer;
            display: inline-block;
            box-sizing: border-box;
            height: 38px;
            line-height: 36px;
            font-size: 14px;
            font-weight: bold;
            text-align: center;
            color: #888888;
            border-radius: 20px;
            padding-left: 15px;
            padding-right: 15px;

            &.on {
              position: relative;
              top: -1px;
              box-shadow: 0 12px 10px -4px rgba(0, 0, 0, 0.2);
              border: solid 1px #ec0147;
              background-color: #ec0147;
              color: #fff;
            }

            &:not(:nth-of-type(1)) {
              margin-left: -5px;
            }
          }
        }
      }
      .box__indicator {
        margin-top: 30px;
        display: flex;
        justify-content: space-between;

        .box-layout {
          width: calc(50% - 15px);
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: space-between;

          .label {
            position: relative;
            display: inline-block;
            width: 38px;
            height: 38px;
            border-radius: 5px;
            margin-right: 22px;

            &.style1 {
              background-color: #9fce12;
            }
            &.style2 {
              background-color: #1199af;
            }

            .emphasis {
              @include positionXYcenter;
              font-size: 20px;
              font-weight: bold;
              color: #ffffff;
            }
          }

          .indicator--wrap {
            width: 40%;
            box-sizing: border-box;
            li {
              white-space: nowrap;
              margin-top: 12px;

              &:nth-of-type(1) {
                margin-top: 0;
              }

              &.largeA {
                .aValue {
                  color: #9fce12;
                }
              }
              &.largeB {
                .bValue {
                  color: #1199af;
                }
              }

              .icon {
                vertical-align: text-top;
                margin-right: 5px;
              }

              .value {
                float: right;
              }
            }
          }

          .divide {
            display: inline-block;
            width: 1px;
            height: 100%;
            border-right: 1px solid #d8d8d8;
          }
        }
      }
    }
    &.content__conversion-rate {
      margin-top: 70px;
      .box-layout {
        margin-top: 30px;
      }
      .title {
        font-size: 22px;
        font-weight: bold;
        color: #444444;
      }
      table {
        width: 100%;
        th {
          font-size: 14px;
          line-height: 1;
          text-align: center;
          color: #ffffff;

          height: 50px;
          &.th__a {
            border-top-left-radius: 5px;
            border-bottom-left-radius: 5px;
            background-color: #9fce12;
          }
          &.th__improve {
            background-color: #555555;
          }
          &.th__b {
            border-top-right-radius: 5px;
            border-bottom-right-radius: 5px;
            background-color: #1199af;
          }
        }
        td {
          height: 90px;
          text-align: center;
          border-bottom: 1px solid #979797;
        }
        .td__a-value {
          border-right: 1px dashed #d7d7d7;

          &.largeA {
            color: #7aa200;
          }
        }
        .td__b-value {
          border-left: 1px dashed #d7d7d7;

          &.largeB {
            color: #1199af;
          }
        }
        .td__separate {
          & > div {
            height: 50%;
            line-height: 45px;

            a {
              font-size: 12px;
              line-height: 1;
              color: #777777;
            }
          }
          .bar--wrap {
            border-top: 1px dashed #d7d7d7;
            position: relative;
            height: 100%;
            .a-bar,
            .b-bar {
              position: relative;
              width: calc(50% - 5px);
              height: 100%;
              display: inline-block;

              i {
                width: 50px;
                @include positionYcenter;
                display: inline-block;
                content: '';
                height: 10px;
                border-radius: 7px;
              }
            }
            .a-bar {
              i {
                right: 1px;
                background-color: #9fce12;
              }
            }
            .b-bar {
              i {
                left: 0;
                background-color: #1199af;
              }
            }
            .divide {
              @include positionXYcenter;
              display: inline-block;
              border-left: 1px solid #979797;
              width: 1px;
              height: 100%;
            }
          }
        }
      }
    }
    &.content__click-per-pv {
      margin-top: 70px;
      .box-layout {
        margin-top: 30px;
      }
      .title {
        font-size: 22px;
        font-weight: bold;
        color: #444444;
      }
      table {
        width: 100%;
        th {
          font-size: 14px;
          line-height: 1;
          text-align: center;
          color: #ffffff;

          height: 50px;
          &.th__a {
            border-top-left-radius: 5px;
            border-bottom-left-radius: 5px;
            background-color: #9fce12;
          }
          &.th__ranking {
            background-color: #555555;
          }
          &.th__b {
            border-top-right-radius: 5px;
            border-bottom-right-radius: 5px;
            background-color: #1199af;
          }
        }
        td {
          text-align: center;
          border-bottom: 1px solid #ebebeb;
          box-sizing: border-box;
        }
        .td__a-value {
          position: relative;
          border-right: 1px solid #ebebeb;
          figure {
            img {
              display: inline-block;
              width: 50px;
              height: 25px;
              background-color: red;
            }
          }
        }
        .td__b-value {
          position: relative;
          border-left: 1px solid #ebebeb;
          figure {
            img {
              display: inline-block;
              width: 50px;
              height: 25px;
              background-color: blue;
            }
          }
        }
        .td__ranking {
          .ranking--wrap {
            display: flex;
            justify-content: center;
            & > div {
              width: calc(100% / 3);
              line-height: 60px;
            }
            .rank {
              position: relative;
              background-color: #ebebeb;
              color: #444;
              font-size: 14px;
              font-weight: bold;
              &:after {
                position: absolute;
                top: 100%;
                left: 0;
                content: '';
                display: inline-block;
                border-bottom: 1px solid #c1c1c1;
                width: 100%;
                height: 1px;
                box-sizing: border-box;
              }
            }
            .a-page-value span,
            .b-page-value span {
              font-size: 13px;
              line-height: 1.23;
              color: #2f2f2f;
            }
            .a-page-value span.largeA {
              color: #9fce12;
            }
            .b-page-value span.largeB {
              color: #1199af;
            }
          }
        }
      }
    }
  }
}
</style>
